import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { FormValue, TaskForm } from "../../../../interfaces/DTO/forms/taskForm";
import { taskFormService } from "../../../../services/forms/taskFormService";
import { expandFields, withDefaults } from "../../../admin/formTemplates/formFieldGeometry";
import TaskFormPage from "./TaskFormPage";

interface Props {
  open: boolean;
  title: string;
  /** the draft (pre-filled) - from taskFormService.draft */
  draft: TaskForm;
  values: Record<string, FormValue>;
  onClose: (values: Record<string, FormValue>) => void;
}

/**
 * A producer's form prepared before its task exists (e.g. in the beneficiaries update dialog): edited on the
 * page, and "התוצאה לחתימה" = the server's render. Nothing is stored here - the values are kept by the caller and
 * saved on the task when it is created.
 */
const DraftFormDialog: React.FC<Props> = ({ open, title, draft, values: initial, onClose }) => {
  const [values, setValues] = useState<Record<string, FormValue>>(initial);
  const [tab, setTab] = useState<"edit" | "result">("edit");
  const [page, setPage] = useState(1);
  const [backgrounds, setBackgrounds] = useState<Record<number, string>>({});
  const [rendered, setRendered] = useState<Record<number, string>>({});
  const [problems, setProblems] = useState<string[]>(draft.problems);

  const fields = useMemo(() => expandFields(draft.fields), [draft.fields]);
  const settings = useMemo(() => withDefaults(draft.settings), [draft.settings]);
  const versionId = draft.template_version_id as number;
  const pageCount = draft.pages.length;
  const contentRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!open) return;
    setValues(initial);
    setTab("edit");
    setPage(1);
    contentRef.current?.scrollTo({ top: 0 });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // the blank pages (cached by the browser)
  useEffect(() => {
    if (!open || !versionId) return;
    let cancelled = false;
    const urls: string[] = [];
    Promise.all(draft.pages.map((_, i) => taskFormService.publishedTemplatePageUrl(versionId, i + 1))).then((list) => {
      urls.push(...list);
      if (!cancelled) setBackgrounds(Object.fromEntries(list.map((u, i) => [i + 1, u])));
    });
    return () => { cancelled = true; urls.forEach(URL.revokeObjectURL); };
  }, [open, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // what blocks sending - checked by the server (after the agent stops typing)
  useEffect(() => {
    if (!open || !versionId) return;
    const timer = setTimeout(() => {
      taskFormService.checkDraft(versionId, values).then(setProblems).catch((): void => undefined);
    }, 500);
    return () => clearTimeout(timer);
  }, [values, open, versionId]);

  // the result tab: the server's render of every page, with the licence holder's saved signature
  useEffect(() => {
    if (!open || tab !== "result") return;
    const urls: string[] = [];
    let cancelled = false;
    setRendered({});
    draft.pages.forEach((_, i) => {
      taskFormService.draftPageUrl(versionId, values, i + 1).then((u) => {
        if (cancelled) { URL.revokeObjectURL(u); return; }
        urls.push(u);
        setRendered((prev) => ({ ...prev, [i + 1]: u }));
      });
    });
    return () => { cancelled = true; urls.forEach(URL.revokeObjectURL); };
  }, [open, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // the page counter follows the scroll: the last page whose top passed a third of the view
  const onScroll = () => {
    const container = contentRef.current;
    if (!container) return;
    const line = container.getBoundingClientRect().top + container.clientHeight / 3;
    let current = 1;
    pageRefs.current.forEach((el, i) => { if (el && el.getBoundingClientRect().top <= line) current = i + 1; });
    setPage(current);
  };

  const scrollToPage = (p: number) => pageRefs.current[p - 1]?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <Dialog open={open} onClose={() => onClose(values)} fullScreen dir="rtl">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="h6" component="span">{draft.template_name ?? "טופס"}</Typography>
        <Typography variant="body2" color="text.secondary" component="span">{title}</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={() => onClose(values)} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers ref={contentRef} onScroll={onScroll} sx={{ bgcolor: "#f4f5fb", pt: 0 }}>
        <Box sx={{ position: "sticky", top: 0, zIndex: 2, bgcolor: "#f4f5fb", mx: -3, px: 3, pt: 1, borderBottom: 1, borderColor: "divider" }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ maxWidth: 1000, mx: "auto" }}>
            <Tabs value={tab} onChange={(_, v) => setTab(v)}>
              <Tab value="edit" label="עריכה" />
              <Tab value="result" label="התוצאה לחתימה" />
            </Tabs>
            <Box sx={{ flex: 1 }} />
            <IconButton size="small" disabled={page <= 1} onClick={() => scrollToPage(page - 1)}><ChevronRightIcon /></IconButton>
            <Typography variant="body2">{`עמוד ${page} מתוך ${pageCount}`}</Typography>
            <IconButton size="small" disabled={page >= pageCount} onClick={() => scrollToPage(page + 1)}><ChevronLeftIcon /></IconButton>
          </Stack>
        </Box>
        <Stack spacing={1.5} sx={{ maxWidth: 1000, mx: "auto", pt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            הטופס ממולא מנתוני הלקוח, הקופה והמוטבים שבחרת. תקנו ישירות על הטופס. הטופס יישמר במשימה וישלח ללקוח לחתימה כשתיצרו את המשימות.
          </Typography>
          {problems.length > 0 && <Alert severity="warning">{`לפני שליחה: ${problems.join(" · ")}`}</Alert>}
          {draft.pages.map((pageSize, i) => (
            <Box key={i} ref={(el: HTMLDivElement | null) => { pageRefs.current[i] = el; }} sx={{ scrollMarginTop: 64 }}>
              {tab === "edit" ? (
                <TaskFormPage
                  imageUrl={backgrounds[i + 1] ?? null}
                  page={i + 1}
                  pageSize={pageSize}
                  fields={fields}
                  values={values}
                  readOnly={false}
                  agentSigned={draft.agent_signed}
                  settings={settings}
                  onChange={(changes) => setValues((prev) => ({ ...prev, ...changes }))}
                />
              ) : rendered[i + 1] ? (
                <Box component="img" src={rendered[i + 1]} alt={`page ${i + 1}`} sx={{ width: "100%", display: "block", boxShadow: 2, bgcolor: "#fff" }} />
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", bgcolor: "#fff", boxShadow: 2, aspectRatio: `${pageSize.width} / ${pageSize.height}` }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="contained" onClick={() => onClose(values)}>סיום</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DraftFormDialog;
